var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

import React, { forwardRef, useCallback, useEffect, useState, Fragment } from "react";
import PropTypes from "prop-types";

import { SelectWrapper, SelectRadioButton, SelectContainer, FocusOverlay } from "./atoms";
import { FieldMessage } from "../FieldMessage";
import { breakPointArrayCleaner } from "../../../utils";

var validTones = {
  danger: "danger",
  warning: "warning",
  success: "success",
  neutral: "neutral"
};

var Select = function Select(_ref) {
  var initialValue = _ref.initialValue,
      onChange = _ref.onChange,
      children = _ref.children,
      direction = _ref.direction,
      gap = _ref.gap,
      name = _ref.name,
      center = _ref.center,
      message = _ref.message,
      _ref$tone = _ref.tone,
      tone = _ref$tone === undefined ? validTones.neutral : _ref$tone,
      reserveMessageSpace = _ref.reserveMessageSpace,
      labelSize = _ref.labelSize,
      rest = _objectWithoutProperties(_ref, ["initialValue", "onChange", "children", "direction", "gap", "name", "center", "message", "tone", "reserveMessageSpace", "labelSize"]);

  var gapArray = gap ? breakPointArrayCleaner(gap === true ? 2 : gap) : false;

  var _useState = useState(),
      value = _useState[0],
      setValue = _useState[1];

  var handleChange = useCallback(function (e) {
    onChange(e);
    setValue(e.target.value);
  }, [onChange]);

  useEffect(function () {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  var childrenWithProps = React.Children.map(children, function (child) {
    if (!child) return;

    return React.cloneElement(child, _extends({}, child.props, {
      gap: gap,
      direction: direction,
      name: name,
      center: center,
      onChange: function onChange(e) {
        return handleChange(e);
      },
      selected: value === child.props.value
    }));
  });

  return React.createElement(
    Fragment,
    null,
    React.createElement(
      SelectWrapper,
      _extends({ direction: direction, gap: gapArray }, rest),
      childrenWithProps
    ),
    React.createElement(FieldMessage, {
      message: message,
      tone: tone,
      reserveMessageSpace: reserveMessageSpace,
      labelSize: labelSize
    })
  );
};

export { Select };
Select.displayName = "Select";

Select.propTypes = process.env.NODE_ENV !== "production" ? {
  onChange: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
  direction: PropTypes.oneOf(["horizontal", "vertical"]),
  gap: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  center: PropTypes.bool,
  message: PropTypes.string,
  tone: PropTypes.string,
  reserveMessageSpace: PropTypes.bool,
  labelSize: PropTypes.size
} : {};

var Option = forwardRef(function (_ref2, ref) {
  var id = _ref2.id,
      value = _ref2.value,
      selected = _ref2.selected,
      onChange = _ref2.onChange,
      required = _ref2.required,
      children = _ref2.children,
      name = _ref2.name,
      disabled = _ref2.disabled,
      rest = _objectWithoutProperties(_ref2, ["id", "value", "selected", "onChange", "required", "children", "name", "disabled"]);

  return React.createElement(
    SelectContainer,
    _extends({
      $as: "label",
      selected: selected,
      htmlFor: id
    }, rest),
    children,
    React.createElement(SelectRadioButton, _extends({
      $as: "input",
      type: "radio",
      value: value,
      onChange: onChange,
      name: name,
      disabled: disabled,
      "aria-disabled": disabled,
      "aria-required": required,
      forwardedRefSafe: ref
    }, rest)),
    React.createElement(FocusOverlay, null)
  );
});

Option.propTypes = process.env.NODE_ENV !== "production" ? {
  value: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func
} : {};

Select.Option = Option;